import React, { useEffect, useState } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'animate.css/animate.min.css';
import './firebase/config';


// Import all pages
import Home from './pages/Home';
import About from './pages/About';
import ServicePage from './pages/ServicePage';
import Cart from './pages/Cart';
import Login from './pages/Login';
import Register from './pages/Register';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Disclaimer from './pages/Disclaimer';
import Refund from './pages/Refund';
import Payment from './pages/Payment';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';
// Import Components
import TopBar from './components/TopBar';
import Header from './components/Header';
import Footer from './components/Footer';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return React.createElement(
        'div',
        { style: { padding: '20px', textAlign: 'center', marginTop: '50px' } },
        React.createElement('h1', null, 'Something went wrong'),
        React.createElement(
          'details',
          { style: { whiteSpace: 'pre-wrap', marginTop: '20px', textAlign: 'left' } },
          React.createElement('summary', null, 'Error Details'),
          React.createElement('p', null, this.state.error && this.state.error.toString()),
          React.createElement('p', null, this.state.errorInfo && this.state.errorInfo.componentStack)
        ),
        React.createElement(
          'button',
          {
            onClick: () => window.location.reload(),
            style: { marginTop: '20px', padding: '10px 20px' }
          },
          'Reload Page'
        )
      );
    }

    return this.props.children;
  }
}

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, [pathname]);

  return null;
};

// Main Layout Wrapper - With Header & Footer
const MainLayout = ({ children }) => {
  return React.createElement(
    React.Fragment,
    null,
    React.createElement(TopBar, null),
    React.createElement(Header, null),
    children,
    React.createElement(Footer, null)
  );
};

// NEW: Layout without Header (for Payment page)
const PaymentLayout = ({ children }) => {
  return React.createElement(
    React.Fragment,
    null,
    children,
    React.createElement(Footer, null)
  );
};

// NEW: Layout without Header & Footer (for full page)
const FullPageLayout = ({ children }) => {
  return React.createElement(
    React.Fragment,
    null,
    children
  );
};

function App() {
  const [isAOSInitialized, setIsAOSInitialized] = useState(false);

  useEffect(() => {
    const initAOS = async () => {
      try {
        if (AOS && typeof AOS.init === 'function') {
          await AOS.init({
            duration: 1000,
            once: false,
            offset: 100,
            easing: 'ease-out-cubic',
            disable: false,
            startEvent: 'DOMContentLoaded',
            initClassName: 'aos-init',
            animatedClassName: 'aos-animate',
            useClassNames: false,
            disableMutationObserver: false,
            debounceDelay: 50,
            throttleDelay: 99,
          });
          setIsAOSInitialized(true);
          console.log('AOS initialized successfully');
        }
      } catch (error) {
        console.error('Error initializing AOS:', error);
      }
    };

    initAOS();
    
    const handleRouteChange = () => {
      if (isAOSInitialized && AOS && typeof AOS.refresh === 'function') {
        setTimeout(() => {
          try {
            AOS.refresh();
          } catch (error) {
            console.error('Error refreshing AOS:', error);
          }
        }, 100);
      }
    };
    
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [isAOSInitialized]);

  return React.createElement(
    ErrorBoundary,
    null,
    React.createElement(
      Router,
      null,
      React.createElement(
        'div',
        { className: 'App' },
        React.createElement(ScrollToTop, null),
        React.createElement(
          Routes,
          null,
          // Pages with Full Layout (Header + Footer)
          React.createElement(Route, {
            path: '/',
            element: React.createElement(MainLayout, null, React.createElement(Home, null))
          }),
          React.createElement(Route, {
            path: '/about',
            element: React.createElement(MainLayout, null, React.createElement(About, null))
          }),
          React.createElement(Route, {
            path: '/service/:id',
            element: React.createElement(MainLayout, null, React.createElement(ServicePage, null))
          }),
          React.createElement(Route, {
            path: '/cart',
            element: React.createElement(MainLayout, null, React.createElement(Cart, null))
          }),
          React.createElement(Route, {
            path: '/login',
            element: React.createElement(MainLayout, null, React.createElement(Login, null))
          }),
          React.createElement(Route, {
            path: '/register',
            element: React.createElement(MainLayout, null, React.createElement(Register, null))
          }),
          React.createElement(Route, {
            path: '/terms',
            element: React.createElement(MainLayout, null, React.createElement(Terms, null))
          }),
          React.createElement(Route, {
            path: '/privacy',
            element: React.createElement(MainLayout, null, React.createElement(Privacy, null))
          }),
          React.createElement(Route, {
            path: '/disclaimer',
            element: React.createElement(MainLayout, null, React.createElement(Disclaimer, null))
          }),
          React.createElement(Route, {
            path: '/refund',
            element: React.createElement(MainLayout, null, React.createElement(Refund, null))
          }),
          React.createElement(Route, {
            path: '/profile',
            element: React.createElement(MainLayout, null, React.createElement(Profile, null))
          }),
          
          // Payment Page - WITHOUT Header (only Footer)
          React.createElement(Route, {
            path: '/payment',
            element: React.createElement(PaymentLayout, null, React.createElement(Payment, null))
          }),
          
          // 404 Page - Without Header & Footer
          React.createElement(Route, {
            path: '*',
            element: React.createElement(FullPageLayout, null, React.createElement(NotFound, null))
          })
        )
      )
    )
  );
}

export default App;